import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  getDatasourceFolders,
  createDatasourceFolder,
  updateDatasourceFolder,
  deleteDatasourceFolder,
  reorderDatasourceFolders,
  deleteDatasources,
  moveDatasourceToFolder,
  type CreateFolderRequest,
  type UpdateFolderRequest,
  type FolderResponse,
} from '@/services/apiClient'
import { dataSourceKeys } from './useDataSources'

// Query keys for folder operations
export const folderKeys = {
  all: ['datasource-folders'] as const,
  lists: () => [...folderKeys.all, 'list'] as const,
  list: (parentId?: string) => [...folderKeys.lists(), { parentId }] as const,
  search: (search?: string) => [...folderKeys.lists(), { search }] as const,
  details: () => [...folderKeys.all, 'detail'] as const,
  detail: (id: string) => [...folderKeys.details(), id] as const,
}

// Get folders with optional parent filter and search
export const useFoldersQuery = (options?: {
  parentFolderId?: string
  search?: string
  page?: number
  limit?: number
  enabled?: boolean
}) => {
  const { parentFolderId, search, page = 1, limit = 100, enabled = true } = options || {}

  return useQuery({
    queryKey: search ? folderKeys.search(search) : folderKeys.list(parentFolderId),
    queryFn: async () => {
      const { data: response } = await getDatasourceFolders({
        query: {
          page,
          limit,
          parentFolderId,
          search,
        },
        throwOnError: true
      })
      return response.data
    },
    enabled: enabled && (() => {
      // Only fetch if user is authenticated
      const token = localStorage.getItem('token')
      const userStr = localStorage.getItem('user')
      return !!(token && userStr)
    })(),
    // 30 seconds
    staleTime: 30 * 1000,
    // 15 minutes
    gcTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
  })
}

// Create folder mutation
export const useCreateFolderMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: CreateFolderRequest) => {
      const { data: responseData } = await createDatasourceFolder({
        body: data,
        throwOnError: true
      })
      return responseData
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: folderKeys.lists() })
    },
  })
}

// Update folder mutation
export const useUpdateFolderMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ folderId, data }: { folderId: string; data: UpdateFolderRequest }) => {
      const { data: responseData } = await updateDatasourceFolder({
        path: { folder_id: folderId },
        body: data,
        throwOnError: true
      })
      return responseData
    },
    onSuccess: (_, { folderId }) => {
      void queryClient.invalidateQueries({ queryKey: folderKeys.lists() })
      void queryClient.invalidateQueries({ queryKey: folderKeys.detail(folderId) })
    },
  })
}

// Delete folder mutation (moves connections to root)
export const useDeleteFolderMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (folderId: string) => {
      await deleteDatasourceFolder({
        path: { folder_id: folderId },
        throwOnError: true
      })
      return folderId
    },
    onSuccess: () => {
      // Invalidate all folder queries since deletion affects counts
      void queryClient.invalidateQueries({ queryKey: folderKeys.all })
      // Also invalidate datasources since they may become uncategorized
      void queryClient.invalidateQueries({ queryKey: dataSourceKeys.lists() })
      void queryClient.invalidateQueries({ queryKey: dataSourceKeys.infinite() })
    },
  })
}

// Delete folder with all its connections and subfolders mutation
export const useDeleteFolderWithConnectionsMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      folderId,
      connectionIds,
      subfolderIds = [],
      subfolderConnectionIds = [],
    }: {
      folderId: string
      connectionIds: string[]
      subfolderIds?: string[]
      subfolderConnectionIds?: string[]
    }) => {
      // Combine all connection IDs (from main folder and all subfolders)
      const allConnectionIds = [...connectionIds, ...subfolderConnectionIds]

      // First, delete all connections in the folder and subfolders
      for (const connectionId of allConnectionIds) {
        await deleteDatasources({
          path: { datasource_id: connectionId },
          throwOnError: true
        })
      }

      // Then delete all subfolders (they should be leaf folders now)
      for (const subfolderId of subfolderIds) {
        await deleteDatasourceFolder({
          path: { folder_id: subfolderId },
          throwOnError: true
        })
      }

      // Finally delete the main folder itself
      await deleteDatasourceFolder({
        path: { folder_id: folderId },
        throwOnError: true
      })

      return {
        folderId,
        deletedConnectionCount: allConnectionIds.length,
        deletedFolderCount: subfolderIds.length + 1,
      }
    },
    onSuccess: () => {
      // Invalidate all folder queries
      void queryClient.invalidateQueries({ queryKey: folderKeys.all })
      // Invalidate datasources queries
      void queryClient.invalidateQueries({ queryKey: dataSourceKeys.lists() })
      void queryClient.invalidateQueries({ queryKey: dataSourceKeys.infinite() })
    },
  })
}

// Bulk reorder folders mutation (with optimistic update for smooth drag-and-drop animation)
export const useReorderFoldersMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (orderedIds: string[]) => {
      const { data } = await reorderDatasourceFolders({
        body: { orderedIds },
        throwOnError: true
      })
      return data
    },
    onMutate: async (orderedIds) => {
      // Cancel outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({ queryKey: folderKeys.lists() })

      // Snapshot the previous value for rollback
      const previousFolders = queryClient.getQueryData(folderKeys.lists())

      // Optimistically update the orderIndex in the folder list cache
      queryClient.setQueryData(folderKeys.lists(), (old: unknown) => {
        const data = old as { id?: string; orderIndex?: number }[] | undefined
        if (!Array.isArray(data)) return old

        return data.map((folder) => {
          const newIndex = orderedIds.indexOf(folder.id || '')
          if (newIndex !== -1) {
            return { ...folder, orderIndex: newIndex }
          }
          return folder
        })
      })

      return { previousFolders }
    },
    onError: (_err, _orderedIds, context) => {
      // Rollback to the previous value on error
      if (context?.previousFolders) {
        queryClient.setQueryData(folderKeys.lists(), context.previousFolders)
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: folderKeys.lists() })
    },
  })
}

// Move datasource to folder mutation
export const useMoveDatasourceToFolderMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ datasourceId, folderId }: { datasourceId: string; folderId: string | null }) => {
      const { data } = await moveDatasourceToFolder({
        path: { datasource_id: datasourceId },
        body: { folderId },
        throwOnError: true
      })
      return data
    },
    onSuccess: () => {
      // Invalidate datasource queries
      void queryClient.invalidateQueries({ queryKey: dataSourceKeys.lists() })
      void queryClient.invalidateQueries({ queryKey: dataSourceKeys.infinite() })
      // Invalidate folder queries since counts change
      void queryClient.invalidateQueries({ queryKey: folderKeys.lists() })
    },
  })
}

// Helper type exports
export type { FolderResponse, CreateFolderRequest, UpdateFolderRequest }
